<style scoped lang="scss">
form {
    h2 {
        color: #000;
    }
    ::v-deep(.formkit-select-icon) {
        height: 50px;
        margin: 0;
        padding: 0;
    }
}
main .container .card {
    background-color: rgba(91, 91, 98, 0.08);
}
</style>

<template>
    <div class="mb-5">
        <h1 class="page-title">{{ title }}</h1>
    </div>
    <div class="max-w-screen-xl mx-auto">
        <div class="flex flex-wrap -mx-4">
        <div class="w-full lg:w-1/4 px-3">
            <div class="mb-5">
            <div class="card w-75 shadow-xl">
                <div class="card-body items-center text-center">
                    <h5 class="card-title">Image Web</h5>
                    <UploadImage :image-preview-url="form.imageWeb ? form.imageWeb.url : getBaseUrl() + '/media/blank2.png'"
                         @image-removed="onImageWebRemoved"
                         @imageUploaded="onImageWebUpload"
                         @error="onImageUploadError"
                    />
                </div>
            </div>
            </div>
            <div class="mb-5">
            <div class="card w-75 shadow-xl">
                <div class="card-body items-center text-center">
                    <h5 class="card-title">Image Mobile</h5>
                    <UploadImage :image-preview-url="form.imageMobile ? form.imageMobile.url : getBaseUrl() + '/media/blank2.png'"
                         @image-removed="onImageMobileRemoved"
                         @imageUploaded="onImageMobileUpload"
                         @error="onImageUploadError"
                    />
                </div>
            </div>
            </div>
        </div>

        <div class="w-full lg:w-3/4 px-4">
        <div class="card w-full shadow-xl">
        <div class="card-body">
            <div class="w-full">
                <FormKit type="form"
                 :actions="false"
                 :model="form"
                 @submit="onSubmit"
                 #default="{ state: { valid } }"
                 outer-class="w-full"
                 incomplete-message="Veuillez remplir tous les champs correctement"
                >
                <FormKit type="text"
                 name="produit"
                 v-model="form.product"
                 label="Produit uuid *"
                 validation="required"
                 :validation-messages="{
                    required: 'Ce champ est obligatoire.'
                }"
                />
                <FormKit type="number"
                 name="position"
                 label="Position *"
                 :value="form.position"
                 v-model="form.position"
                 validation="required|min:1"
                 :validation-messages="{
                    required: 'Ce champ est obligatoire.',
                    min: 'Ce champ doit être un nombre décimal superieur à 0',
                }"
                />
                <div class="card-actions">
                    <div v-if="loading === false">
                        <FormKit type="submit"
                         :label="submitBtnLabel"
                         :disabled="!valid"
                         :class="{ 'btn-primary': true, 'btn-block': true }"
                        />
                    </div>
                    <FormKit type="button"
                     label="Annuler"
                     @click="onCancel"
                     :class="{ 'btn-secondary': true, 'btn-block': true }"
                    />
                    </div>
                </FormKit>
            </div>
        </div>
        </div>
        </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue';
import { useRoute } from 'vue-router';
import ApiService from "@/_services/api.service";
import swal from "@/_helpers/swal";
import { useRouter } from "vue-router";
import UploadImage from "@/components/forms/UploadImage.vue";
import {fetchNewsByUuid} from "@/_services/homepage/news";
import {INews} from "@/models/homepage/INews";
const title = ref<string>("Edition d'une nouveauté");
const submitBtnLabel = 'Editer';
const news = ref<INews>({} as INews);
const route = useRoute();
const uuidRouteParam = ref<string>("");
const router = useRouter();
const loading = ref<boolean>(true);

onBeforeMount(async () => {
    loading.value = true;
    uuidRouteParam.value = route.params.uuid.length ? route.params.uuid as string : "";
    if (uuidRouteParam.value.length) {
        news.value = await fetchNewsByUuid(uuidRouteParam.value);
        await hydrateNewsForm(news.value);
        loading.value = false;
    }
});

const form = ref<INews>({
    position: null,
    imageWeb: '',
    imageMobile: '',
    product: ''
});

const hydrateNewsForm = (news: INews) => {
    form.value = {
        position: news.position,
        imageWeb: news.imageWeb,
        imageMobile: news.imageMobile,
        product: news.product
    }
}

const onSubmit = async (formData) => {
    try {
        if (null == form.value.imageWeb || null == form.value.imageMobile) {
            swal.warning('Les deux images sont obligatoires.');
            return false;
        }
        const payload = {
            position: parseInt(formData.position),
            product: null !== form.value.product ? `/api/master-product/${form.value.product}` : null,
            imageWeb: null !== form.value.imageWeb ? `/api/images/${form.value.imageWeb.uuid}` : null,
            imageMobile: null !== form.value.imageMobile ? `/api/images/${form.value.imageMobile.uuid}` : null
        }
        swal.confirm(`Êtes-vous sûr de vouloir éditer ?`,
            "Annuler",
            submitBtnLabel
        )
            .then((results) => results.isConfirmed && updateNews(payload))
    } catch (err: any) {
        swal.error(err.errors[0]);
    }
}

const updateNews = (data) => {
    if(uuidRouteParam.value.length) {
        ApiService.patch('news', uuidRouteParam.value, data)
            .then(() => {
                loading.value = false;
                swal.info("Edition avec succès")
                    .then((result) => {
                        if (result.isConfirmed) {
                            router.push({
                                name: "newsList"
                            });
                        }
                    });
            })
            .catch((axiosError) => {
            swal.error("Une erreur est survenue lors de la modification");
            axiosError.response.data.errors.forEach((error) => {
                if (error.message.startsWith("Item not found for")
                    || error.message.startsWith("Could not convert database value")) {
                    swal.error("Une erreur est survenue au moment de la modification, l’uuid ajoutée n’existe pas.");
                    return false;
                }
            });
        });
    }
}

const onCancel = () => {
    swal.confirm(
        "Êtes-vous sûr de vouloir annuler l'édition ?",
        "Oui",
        "Non"
    ).then((result) => {
        if (result.isDismissed) {
            router.push({
                name: "newsList"
            });
        }
    });
}

const onImageWebUpload = (image) => {
    form.value.imageWeb = image;
}

const onImageMobileUpload = (image) => {
    form.value.imageMobile = image;
}

const onImageWebRemoved = () => {
    form.value.imageWeb = null;
}

const onImageMobileRemoved = () => {
    form.value.imageMobile = null;
}

const onImageUploadError = (errorMsg: string) => {
    swal.error(errorMsg);
}

function getBaseUrl() {
    return window.location.origin;
}
</script>